import {
  Button,
  Container,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  useQueryErrorResetBoundary,
} from '@tanstack/react-query';
import {
  ErrorComponent,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  HTTPError,
  type NormalizedOptions,
} from 'ky';
import React, {
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  useAuth,
} from 'react-oidc-context';

// import {
//   TraceWrapper,
// } from '#src/helpers/otel/trace';
import {
  type RootRouterAuthContext,
} from '#src/types/auth';

export class QueryNotFoundError extends HTTPError {
  constructor(
    res: Response,
    req: Request,
    options: NormalizedOptions,
  ) {
    super(
      res,
      req,
      options,
    );
    console.error(`Fetch URL not found: ${res.status}`, req, res, options);
  }
}
export class QueryNotAuthorizedError extends HTTPError {
  constructor(
    res: Response,
    req: Request,
    options: NormalizedOptions,
  ) {
    super(
      res,
      req,
      options,
    );
    console.error(`Fetch not authorized : ${res.status}`, req, res, options);
  }
}

/**
 * Function to determine if request errors should skip retries.
 * Will return true for the following HTTP status codes:
 * 400: Bad Request
 * 401: Unauthorized
 * 403: Forbidden
 *
 * @export
 * @param {HTTPError} error
 * @return {*}  {boolean}
 */
export function shouldSkipRetry(error: HTTPError): boolean {
  if (!error.response) {
    console.warn('Query error without response object');
    return false;
  } else if (
    error.response.status === 400
    || error.response.status === 401
    || error.response.status === 403
  ) {
    return true;
  } else {
    return false;
  }
};

export async function refreshAuthToken(
  auth: RootRouterAuthContext,
  router: ReturnType<typeof useRouter>,
  refreshingTokenState: [boolean, Dispatch<SetStateAction<boolean>>],
): Promise<void> {
  const [
    refreshingToken,
    setRefreshingToken,
  ] = refreshingTokenState;
  const location = router.state.location;
  const url_state = location.href;
  // Return if there is no user session present to renew.
  if (!auth.user || auth.isLoading) {
    if (auth.user) {
      console.warn('Auth not ready');
    } else {
      console.warn('No user session');
      auth.signinRedirect({
        url_state,
      });
    }
    return;
  }
  // Attempt to renew access token using refresh token
  if (!refreshingToken && auth.user.refresh_token) {
    try {
      console.warn('Refreshing access token');
      setRefreshingToken(true);
      // auth.startSilentRenew();
      await auth.signinSilent();
      console.log('Silent token renewal started.');
    } catch {
      // If refreshing token fails, redirect to login page
      console.warn('Refreshing access token failed');
      auth.signinRedirect({
        url_state,
      });
    } finally {
      setRefreshingToken(false);
    }
  }
};

export function QueryError({
  error,
}: ErrorComponentProps): React.ReactNode {
  console.warn(error);
  const router = useRouter();
  const auth = useAuth() as unknown as RootRouterAuthContext;
  const refreshingTokenState = useState<boolean>(false);

  if (error instanceof HTTPError) {
    if (shouldSkipRetry(error)) {
      refreshAuthToken(
        auth,
        router,
        refreshingTokenState,
      );
    }
  }

  return (
    <Container>
      <Stack>
        <Text
          size="xl"
          fw={ 800 }
          variant="gradient"
        >
          Error!
        </Text>
        <Text
          c="white"
        >
          { error.message }
        </Text>
        <Button
          onClick={ () => {
            router.invalidate();
          } }
        >
          Retry
        </Button>
        { /* <ErrorComponent error={error} /> */ }
      </Stack>
    </Container>
  );
}
